import { api } from './api';

function arrayBufferToBase64(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

export const VAPID_PUBLIC_KEY = 'BNaet4oZYVJgXTDhB5kMj723x515NS-viG_pxHLX0Qcx9jhWLIKfZn5rtDSNOVMGNcxpVBRjAeSSUdLIxDQ8gkg';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function isPushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    return reg;
  } catch (err) {
    console.error('Service worker registration failed:', err);
    return null;
  }
}

export async function getCurrentPushSubscription(): Promise<PushSubscription | null> {
  const reg = await registerServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function enablePushNotifications(): Promise<{ success: boolean; error?: string }> {
  if (!isPushSupported()) {
    return { success: false, error: 'Web Push is not supported by this browser.' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return {
      success: false,
      error: permission === 'denied'
        ? 'Notification permission was denied. Please allow notifications in your browser site settings.'
        : 'Notification permission was dismissed.',
    };
  }

  const reg = await registerServiceWorker();
  if (!reg) {
    return { success: false, error: 'Could not register background notification service.' };
  }

  try {
    const convertedVapidKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
    let sub = await reg.pushManager.getSubscription();

    if (sub) {
      const p256dhKey = sub.getKey('p256dh');
      // If existing subscription is missing keys or has expired, unsubscribe and re-subscribe cleanly
      if (!p256dhKey) {
        await sub.unsubscribe().catch(() => {});
        sub = null;
      }
    }

    if (!sub) {
      try {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey as unknown as BufferSource,
        });
      } catch (subErr: any) {
        // Handle mismatch from legacy subscription
        const existing = await reg.pushManager.getSubscription();
        if (existing) {
          await existing.unsubscribe().catch(() => {});
        }
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: convertedVapidKey as unknown as BufferSource,
        });
      }
    }

    const p256dhKey = sub.getKey('p256dh');
    const authKey = sub.getKey('auth');

    const p256dh = arrayBufferToBase64(p256dhKey);
    const auth = arrayBufferToBase64(authKey);

    await api.admin.subscribePush({
      endpoint: sub.endpoint,
      p256dh: p256dh || 'native_fcm',
      auth: auth || 'native_fcm',
      userAgent: navigator.userAgent,
    });

    return { success: true };
  } catch (err: any) {
    console.error('Web Push subscription failed:', err);
    return { success: false, error: err.message || 'Failed to subscribe to browser push.' };
  }
}

export async function disablePushNotifications(): Promise<{ success: boolean; error?: string }> {
  try {
    const sub = await getCurrentPushSubscription();
    if (sub) {
      await api.admin.unsubscribePush(sub.endpoint).catch(() => {});
      await sub.unsubscribe();
    }
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to unsubscribe.' };
  }
}
